// Define the interface locally to avoid import issues
interface CartCustomOption {
  id: number
  name: string
  price: number
  quantity: number
}

interface CartCustomization {
  id: number
  name: string
  options: CartCustomOption[]
}

/**
 * Formats item details by replacing HTML entities
 * @param text The text to format
 * @returns Formatted text
 */
export function formatItemDetails(text: string | undefined): string {
  if (!text) return ""
  return text.replace(/&nbsp;/g, " ")
}

/**
 * Checks if two sets of customizations are equal
 * @param customizations1 First set of customizations
 * @param customizations2 Second set of customizations
 * @returns True if customizations are identical
 */
export const areCustomizationsEqual = (
  customizations1?: CartCustomization[],
  customizations2?: CartCustomization[]
): boolean => {
  if (!customizations1 && !customizations2) return true
  if (!customizations1 || !customizations2) return false
  if (customizations1.length !== customizations2.length) return false

  // Sort both arrays to ensure consistent comparison
  const sorted1 = [...customizations1].sort((a, b) => a.id - b.id)
  const sorted2 = [...customizations2].sort((a, b) => a.id - b.id)

  // Compare each customization
  for (let i = 0; i < sorted1.length; i++) {
    const c1 = sorted1[i]
    const c2 = sorted2[i]

    if (c1.id !== c2.id) return false

    // Compare options
    if (!c1.options && !c2.options) continue
    if (!c1.options || !c2.options) return false
    if (c1.options.length !== c2.options.length) return false

    // Sort options
    const sortedOptions1 = [...c1.options].sort((a, b) => a.id - b.id)
    const sortedOptions2 = [...c2.options].sort((a, b) => a.id - b.id)

    // Compare each option
    for (let j = 0; j < sortedOptions1.length; j++) {
      const o1 = sortedOptions1[j]
      const o2 = sortedOptions2[j]

      if (
        o1.id !== o2.id ||
        o1.quantity !== o2.quantity ||
        o1.price !== o2.price
      ) {
        return false
      }
    }
  }

  return true
}
